import React, { useState } from 'react';
import { 
  Building2, 
  FileText, 
  Users, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  Home,
  Briefcase,
  Tag,
  CheckCircle,
  Target,
  Package,
  DollarSign,
  Shield,
  Crown,
  ChevronDown,
  ChevronRight,
  Search,
  Calendar,
  User,
  Coins,
  HelpCircle,
  MessageCircle
} from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';
import TenderSearch from './TenderSearch';
import PrivacyPolicy from './PrivacyPolicy';

interface LayoutProps {
  children: React.ReactNode;
  currentPage: string;
  onPageChange: (page: string) => void;
}

const Layout: React.FC<LayoutProps> = ({ children, currentPage, onPageChange }) => {
  const { currentCompany, currentUser, logout } = useTenant();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [configurationOpen, setConfigurationOpen] = useState(false);
  const [showTenderSearch, setShowTenderSearch] = useState(false);
  const [showHelpChat, setShowHelpChat] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [, ] = useState(false); // Removed unused state

  // Verificar si es la empresa Licitatrak (super admin)
  const isSuperAdmin = currentCompany?.rut === '99.999.999-9';
  
  // Obtener información de la versión de la empresa
  const isLite = currentCompany?.islite === true;
  const versionText = isLite ? 'lite' : 'pro';

  const mainNavigation = [
    { id: 'dashboard', name: 'Dashboard', icon: Home },
    { id: 'applications-kanban', name: 'Postulaciones', icon: Target },
    { id: 'calendar', name: 'Calendario', icon: Calendar },
    { id: 'tenders', name: 'Licitaciones', icon: FileText },
    { id: 'organizations', name: 'Organizaciones', icon: Building2 },
    { id: 'tender-types', name: 'Tipos de Licitación', icon: Tag },
    { id: 'products', name: 'Productos', icon: Package },
    { id: 'currency-rates', name: 'Tasas de Cambio', icon: DollarSign },
    { id: 'employees', name: 'Empleados', icon: Users },
    { id: 'account', name: 'Mi Perfil', icon: User }
  ];

  // Elementos de configuración solo para super admin
  const configurationItems = [
    { id: 'tender-statuses', name: 'Estados de Licitación', icon: CheckCircle },
    { id: 'application-statuses', name: 'Estados de Postulación', icon: Briefcase },
    { id: 'currencies', name: 'Divisas', icon: Coins },
    { id: 'companies', name: 'Gestión de Empresas', icon: Shield },
    { id: 'global-employees', name: 'Empleados Globales', icon: Users },
    { id: 'plans', name: 'Gestión de Planes', icon: Crown }
  ];

  const handleLogout = async () => {
    try {
      console.log('Iniciando proceso de logout...');
      await logout();
      console.log('Logout exitoso, recargando página...');
      // Force a page reload to ensure complete cleanup
      window.location.reload();
    } catch (error) {
      console.error('Error durante logout:', error);
      // Even if there's an error, force reload to clear everything
      window.location.reload();
    }
  };

  const handleOpenTenderSearch = () => {
    setShowTenderSearch(true);
    setSidebarOpen(false);
  };

  const handleOpenBolsaLicitaciones = () => {
    onPageChange('account');
    setActiveTab('bolsa-licitaciones');
    setSidebarOpen(false);
  };

  const setActiveTab = (tab: string) => {
    // This function will be used by the AccountSettings component
    localStorage.setItem('accountActiveTab', tab);
  };

  const handleCloseTenderSearch = () => {
    setShowTenderSearch(false);
  };

  const handleOpenHelpChat = () => {
    setShowHelpChat(true);
  };

  const handleCloseHelpChat = () => {
    setShowHelpChat(false);
  };

  const handleOpenPrivacyPolicy = () => {
    setShowPrivacyPolicy(true);
  };

  const handleClosePrivacyPolicy = () => {
    setShowPrivacyPolicy(false);
  };

  const isConfigurationPage = configurationItems.some(item => item.id === currentPage);

  // Auto-abrir configuración si estamos en una página de configuración
  React.useEffect(() => {
    if (isConfigurationPage && isSuperAdmin) {
      setConfigurationOpen(true);
    }
  }, [isConfigurationPage, isSuperAdmin]);

  // Si se está mostrando la búsqueda de licitaciones, renderizar solo ese componente
  if (showTenderSearch) {
    return <TenderSearch onClose={handleCloseTenderSearch} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile menu overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <div className="flex items-center">
            <Briefcase className="h-8 w-8 text-blue-600" />
            <span className="ml-2 text-xl font-bold text-gray-900">
              LicitaTrack
              <span className={`text-xs font-normal ml-1 ${
                isLite ? 'text-gray-500' : 'text-blue-600'
              }`}>
                {versionText}
              </span>
            </span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="px-6 py-4 border-b border-gray-200">
          <div className="text-sm font-medium text-gray-900">{currentCompany?.name}</div>
          <div className="text-xs text-gray-500">{currentUser?.email}</div>
          {isSuperAdmin && (
            <div className="text-xs text-purple-600 font-medium mt-1">Super Administrador</div>
          )}
        </div>

        {/* Búsqueda de Licitaciones */}
        <div className="px-4 py-4 border-b border-gray-200 space-y-2">
          <div>
            <button
              onClick={handleOpenTenderSearch}
              className="w-full flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              <Search className="h-5 w-5 mr-3" />
              Buscar en Mercado Público
            </button>
          </div>
          
          {isSuperAdmin && (
            <div>
              <button
                onClick={handleOpenBolsaLicitaciones}
                className="w-full flex items-center px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
              >
                <Package className="h-5 w-5 mr-3" />
                Bolsa de Licitaciones
              </button>
            </div>
          )}
        </div>
        <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
          {/* Navegación principal */}
          {mainNavigation.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => {
                  onPageChange(item.id);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  currentPage === item.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <Icon className="h-5 w-5 mr-3" />
                {item.name}
              </button>
            );
          })}

          {/* Sección de Configuración - Solo para Super Admin */}
          {isSuperAdmin && (
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={() => setConfigurationOpen(!configurationOpen)}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isConfigurationPage
                    ? 'bg-purple-100 text-purple-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center">
                  <Settings className="h-5 w-5 mr-3" />
                  Configuración
                </div>
                {configurationOpen ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>

              {/* Elementos de configuración */}
              {configurationOpen && (
                <div className="ml-6 mt-2 space-y-1">
                  {configurationItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        onClick={() => {
                          onPageChange(item.id);
                          setSidebarOpen(false);
                        }}
                        className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                          currentPage === item.id
                            ? 'bg-purple-100 text-purple-700'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                      >
                        <Icon className="h-4 w-4 mr-3" />
                        {item.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </nav>

        <div className="px-4 py-4 border-t border-gray-200 space-y-2">
          <button
            onClick={handleOpenPrivacyPolicy}
            className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 rounded-lg transition-colors"
          >
            <Shield className="h-5 w-5 mr-3" />
            Políticas de Privacidad
          </button>
          <button
            onClick={handleOpenHelpChat}
            className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 rounded-lg transition-colors"
          >
            <MessageCircle className="h-5 w-5 mr-3" />
            Centro de Ayuda
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 rounded-lg transition-colors"
          >
            <LogOut className="h-5 w-5 mr-3" />
            Cerrar Sesión
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col lg:pl-0">
        {/* Top bar */}
        <div className="lg:hidden flex items-center justify-between h-16 px-4 bg-white border-b border-gray-200">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="text-lg font-semibold text-gray-900">
            LicitaTrack
            <span className={`text-xs font-normal ml-1 ${
              isLite ? 'text-gray-500' : 'text-blue-600'
            }`}>
              {versionText}
            </span>
          </div>
          <button
            onClick={handleOpenTenderSearch}
            className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <Search className="h-6 w-6" />
          </button>
        </div>

        {/* Page content */}
        <main className="flex-1 p-6 pb-20">
          {children}
        </main>
      </div>

      {/* Mobile bottom navigation - Visible for testing */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-2 z-50 shadow-lg">
        <div className="flex items-center justify-around">
          <button
            onClick={() => onPageChange('dashboard')}
            className={`flex flex-col items-center py-2 px-3 rounded-lg transition-colors ${
              currentPage === 'dashboard'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <Home className="h-5 w-5 mb-1" />
            <span className="text-xs font-medium">Inicio</span>
          </button>
          
          <button
            onClick={() => onPageChange('tenders')}
            className={`flex flex-col items-center py-2 px-3 rounded-lg transition-colors ${
              currentPage === 'tenders'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <FileText className="h-5 w-5 mb-1" />
            <span className="text-xs font-medium">Licitaciones</span>
          </button>
          
          <button
            onClick={() => onPageChange('applications')}
            className={`flex flex-col items-center py-2 px-3 rounded-lg transition-colors ${
              currentPage === 'applications'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <Briefcase className="h-5 w-5 mb-1" />
            <span className="text-xs font-medium">Postulaciones</span>
          </button>
          
          <button
            onClick={() => onPageChange('account')}
            className={`flex flex-col items-center py-2 px-3 rounded-lg transition-colors ${
              currentPage === 'account'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <User className="h-5 w-5 mb-1" />
            <span className="text-xs font-medium">Perfil</span>
          </button>
          
          <button
            onClick={handleOpenHelpChat}
            className="flex flex-col items-center py-2 px-3 rounded-lg transition-colors text-gray-600 hover:text-gray-900 hover:bg-gray-50"
          >
            <MessageCircle className="h-5 w-5 mb-1" />
            <span className="text-xs font-medium">Ayuda</span>
          </button>
        </div>
      </div>

      {/* Help Chat Modal */}
      {showHelpChat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                  <MessageCircle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Centro de Ayuda</h2>
                  <p className="text-sm text-gray-500">Estamos aquí para ayudarte</p>
                </div>
              </div>
              <button
                onClick={handleCloseHelpChat}
                className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="text-center space-y-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mb-4">
                  <HelpCircle className="w-8 h-8 text-white" />
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">¿Necesitas ayuda?</h3>
                  <p className="text-gray-600 mb-4">
                    Nuestro equipo está disponible para resolver tus dudas y ayudarte a aprovechar al máximo LicitaTrack.
                  </p>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => {
                      window.open('mailto:contacto@licitatrak.cl', '_blank');
                      handleCloseHelpChat();
                    }}
                    className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <MessageCircle className="h-5 w-5 mr-2" />
                    Enviar Email
                  </button>
                  
                  <button
                    onClick={() => {
                      // Abrir el widget de Tawk.to
                      if (window.Tawk_API) {
                        window.Tawk_API.toggle();
                      } else {
                        alert('El chat en vivo se está cargando. Por favor, inténtalo de nuevo en unos segundos.');
                      }
                    }}
                    className="w-full flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <MessageCircle className="h-5 w-5 mr-2" />
                    Chat en Vivo
                  </button>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">Información de Contacto</h4>
                  <div className="text-sm text-blue-700 space-y-1">
                    <p><strong>Email:</strong> contacto@licitatrak.cl</p>
                    <p><strong>Horario:</strong> Lunes a Viernes, 9:00 - 18:00</p>
                    <p><strong>Tiempo de respuesta:</strong> Máximo 24 horas</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      <PrivacyPolicy 
        isOpen={showPrivacyPolicy} 
        onClose={handleClosePrivacyPolicy} 
      />
    </div>
  );
};

export default Layout;